import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import moment from 'moment';
import { DataGrid, GridToolbarContainer, GridToolbarExport, gridClasses } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import PageShell from '../../themes/components/PageShell';
import PageHeader from '../../themes/components/PageHeader';
import PageContent from '../../themes/components/PageContent';
import { GetRegisterTransactionReport } from '../../REST/report';
import { getAmount } from '../../helpers/amount';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import VisibilityIcon from '@mui/icons-material/Visibility';

const StyledDataGrid = styled(DataGrid)(() => ({
    "& .MuiDataGrid-cell": { lineHeight: "unset !important", maxHeight: "none !important", whiteSpace: "normal" },
    "& .MuiDataGrid-row": { maxHeight: "none !important", cursor: "pointer !important" },
}));

function CustomToolbar() {
    return (
        <GridToolbarContainer className={gridClasses.toolbarContainer}>
            <GridToolbarExport csvOptions={{ fileName: "RevjetTransactions" }} printOptions={{ disableToolbarButton: true }} />
        </GridToolbarContainer>
    );
}

const formatDate = (params) => {
    try { return new Date(params.value).toLocaleString() } catch (ex) { return params.value }
}

const amountFormat = (params) => getAmount(params.value)

const columns = [
    { field: 'createdAt', headerName: 'Date', flex: 0.6, valueFormatter: formatDate },
    { field: 'tenantName', headerName: 'Company', flex: 0.8 },
    { field: 'invoiceNumber', headerName: 'Invoice #', flex: 0.6 },
    { field: 'method', headerName: 'Method', flex: 0.5 },
    { field: 'gateway', headerName: 'Gateway', flex: 0.5 },
    { field: 'amount', headerName: 'Amount', flex: 0.5, renderCell: amountFormat },
    { field: 'status', headerName: 'Status', flex: 0.5 },
]

export default function Transactions() {
    const navigate = useNavigate()
    const [begin, setBegin] = useState(moment().startOf('month').format('YYYY-MM-DD'))
    const [end, setEnd] = useState(moment().endOf('day').format('YYYY-MM-DD'))
    const [rows, setRows] = useState([])
    const [byMethod, setByMethod] = useState([])
    const [totalAmount, setTotalAmount] = useState(0)
    const [loading, setLoading] = useState(false)
    
    // Details Dialog States
    const [selectedTx, setSelectedTx] = useState(null)
    const [detailsOpen, setDetailsOpen] = useState(false)

    const handleRowClick = (params) => {
        setSelectedTx(params.row)
        setDetailsOpen(true)
    }

    const gridColumns = [
        ...columns,
        {
            field: 'actions',
            headerName: 'Action',
            flex: 0.3,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <IconButton 
                    color="primary" 
                    size="small" 
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(params);
                    }}
                >
                    <VisibilityIcon fontSize="small" />
                </IconButton>
            )
        }
    ]

    const fetchTransactions = useCallback(async () => {
        setLoading(true)
        const result = await GetRegisterTransactionReport({
            begin: moment(begin).startOf('day').toISOString(),
            end: moment(end).endOf('day').toISOString()
        })
        const transactions = (result.data?.transactions || []).map(t => {
            // Group multi-invoice references cleanly in UI table column
            let invoiceNumbers = '';
            if (Array.isArray(t.invoiceIds) && t.invoiceIds.length > 0) {
                invoiceNumbers = t.invoiceIds.map(x => x.invoiceNumber).filter(Boolean).join(', ');
            } else {
                invoiceNumbers = t.invoiceId?.invoiceNumber || '';
            }

            return {
                ...t,
                id: t._id,
                tenantName: t.tenantId?.name || '',
                invoiceNumber: invoiceNumbers
            }
        }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        setRows(transactions)
        setByMethod(result.data?.byMethod || [])
        setTotalAmount(result.data?.totalAmount || 0)
        setLoading(false)
    }, [begin, end])

    useEffect(() => { fetchTransactions() }, [fetchTransactions])

    return (
        <PageShell>
            <PageHeader title="Transactions" back onBack={() => navigate(-1)} />
            <PageContent fill sx={{ overflowX: 'hidden' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0, gap: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        <TextField
                            type="date"
                            label="From"
                            value={begin}
                            onChange={e => setBegin(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: { xs: '100%', md: '20%' } }}
                        />
                        <TextField
                            type="date"
                            label="To"
                            value={end}
                            onChange={e => setEnd(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: { xs: '100%', md: '20%' } }}
                        />
                        {byMethod.map(m => (
                            <Box key={m.method} sx={{ px: 2, py: 1, borderRadius: 1, border: '1px solid #ddd' }}>
                                <div style={{ fontSize: 12, color: '#666' }}>{m.method} ({m.count})</div>
                                <div style={{ fontWeight: 600 }}>{getAmount(m.total)}</div>
                            </Box>
                        ))}
                        <Box sx={{ px: 2, py: 1, borderRadius: 1, border: '1px solid #ddd', ml: 'auto' }}>
                            <div style={{ fontSize: 12, color: '#666' }}>Total ({rows.length})</div>
                            <div style={{ fontWeight: 600 }}>{getAmount(totalAmount)}</div>
                        </Box>
                    </Box>
                    <Box sx={{ flex: '1 1 auto', minHeight: 0, minWidth: 0 }}>
                        <StyledDataGrid
                            rows={rows}
                            columns={gridColumns}
                            loading={loading}
                            pageSize={25}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            onRowClick={(params) => handleRowClick(params)}
                            components={{ Toolbar: CustomToolbar }}
                        />
                    </Box>
                </Box>
            </PageContent>

            {/* Transaction Details Dialog */}
            <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, borderBottom: '1px solid #ddd', pb: 1.5 }}>
                    Transaction Detailed View
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {selectedTx && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Transaction Date</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {moment(selectedTx.createdAt).format('MM/DD/YYYY hh:mm A')}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Status</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: selectedTx.status === 'Completed' ? 'success.main' : 'warning.main' }}>
                                            {selectedTx.status}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Tenant / Company</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedTx.tenantName || 'N/A'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Invoice Number</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedTx.invoiceNumber || 'N/A'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Method</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedTx.method || 'N/A'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Gateway</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedTx.gateway || 'N/A'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Amount</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: selectedTx.direction === 'Credit' ? 'error.main' : 'success.main' }}>
                                            {selectedTx.direction === 'Credit' ? '-' : ''}{getAmount(selectedTx.amount)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Confirmation #</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                            {selectedTx.gatewayConfirmation || 'N/A'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Performed By</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedTx.performedBy || 'N/A'}</Typography>
                                    </Box>
                                    <Box sx={{ gridColumn: 'span 2' }}>
                                        <Typography variant="caption" color="text.secondary">Payer Details</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {selectedTx.payerName ? `${selectedTx.payerName} (${selectedTx.payerEmail || 'No Email'})` : 'N/A'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>

                            {/* Line Items Paid Section */}
                            {((selectedTx.invoiceId?.lineItems && Array.isArray(selectedTx.invoiceId.lineItems)) || (selectedTx.invoiceIds && selectedTx.invoiceIds.some(inv => inv.lineItems && inv.lineItems.length > 0))) && selectedTx.lineItemUids && selectedTx.lineItemUids.length > 0 && (
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }} color="primary">
                                        Line Items Paid in this Transaction
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {/* Pull items from primary linked invoice or list of multi-linked invoices */}
                                        {(() => {
                                            const allInvoices = [
                                                selectedTx.invoiceId,
                                                ...(Array.isArray(selectedTx.invoiceIds) ? selectedTx.invoiceIds : [])
                                            ].filter(Boolean);

                                            // Flatten all populated line items across invoices
                                            const allLineItems = [];
                                            const seenUids = new Set();
                                            allInvoices.forEach(inv => {
                                                if (Array.isArray(inv.lineItems)) {
                                                    inv.lineItems.forEach(item => {
                                                        if (!seenUids.has(item.uid)) {
                                                            seenUids.add(item.uid);
                                                            allLineItems.push({
                                                                ...item,
                                                                invoiceNumber: inv.invoiceNumber
                                                            });
                                                        }
                                                    });
                                                }
                                            });

                                            return allLineItems
                                                .filter(item => selectedTx.lineItemUids.includes(item.uid))
                                                .map(item => (
                                                    <Box key={item.uid} sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0', pb: 0.5 }}>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.name}</Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {item.description} ({item.invoiceNumber || 'No Inv#'})
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {getAmount(item.charges * item.amount)}
                                                        </Typography>
                                                    </Box>
                                                ));
                                        })()}
                                    </Box>
                                </Paper>
                            )}

                            {/* Error Reason Banner */}
                            {selectedTx.status === 'Failed' && selectedTx.reason && (
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'error.light', borderColor: 'error.main' }}>
                                    <Typography variant="caption" color="error.dark" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                                        FAILURE REASON
                                    </Typography>
                                    <Typography variant="body2" color="error.dark" sx={{ fontWeight: 600 }}>
                                        {selectedTx.reason}
                                    </Typography>
                                </Paper>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid #ddd', px: 2, py: 1.5 }}>
                    <Button onClick={() => setDetailsOpen(false)} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </PageShell>
    )
}
